/**
 * Project Permits Type Definitions (Phase 6)
 *
 * Frontend types for permit tracking system.
 * Matches backend schemas in schemas/project_permits.py
 */

/**
 * Permit types (from Jamie's feedback)
 */
// QW-5: Added 'Time Limited Development Permit' (Christa feedback 2024-12-22)
// 9.16c: Added 'LCRB Job' for Banks Architecture (BC Liquor and Cannabis Regulation Branch)
export type PermitType =
  | 'Building Permit'
  | 'Development Permit'
  | 'DB Permit'
  | 'Occupant Load'
  | 'Private Patio DP'
  | 'Time Limited Development Permit'
  | 'Inquiry'
  | 'Alternate Solution'
  | 'Awning'
  | 'Signage'
  | 'LCRB Job'
  | 'LCRB Annual Renewal'
  | 'Other';

/**
 * Recurring renewal periods for permits
 */
export type RecurringPeriod =
  | 'Every 6 months'
  | 'Annual'
  | 'Every 18 months'
  | 'Every 2 years'
  | 'Every 3 years'
  | 'Every 5 years';

/**
 * Permit status (derived from date fields)
 */
export type PermitStatus = 'not_submitted' | 'submitted' | 'issued' | 'closed';

/**
 * Base permit interface (shared fields)
 */
export interface PermitBase {
  permit_type: PermitType;
  case_number?: string | null;
  permit_number?: string | null;
  reviewer_name?: string | null;
  date_submitted?: string | null; // ISO date string
  date_issued?: string | null; // ISO date string
  date_closed?: string | null; // ISO date string

  // V008 fields - Phase 6 Batch 2
  due_date?: string | null; // ISO date string - Permit expiration/renewal date
  recurring_period?: RecurringPeriod | null; // Recurring renewal period (enum)
  fees_costs?: number | null; // Permit fees/costs in dollars

  notes?: string | null;

  // V052: Applicant responsible (9.9e)
  // NULL/undefined = Banks/Self is responsible (default)
  // UUID = External organization is responsible
  applicant_organization_id?: string | null;

  // V131: Primary recipient email for renewal reminders
  reminder_email?: string | null;
}

/**
 * Permit creation request (frontend → backend)
 */
export interface PermitCreate extends PermitBase {
  // All fields from PermitBase

  // 9.16b: Permit type definition reference and custom data
  permit_type_definition_id?: string | null;
  custom_data?: Record<string, unknown> | null;

  // V131: Custom reminder thresholds (days before due date); backend defaults to [30,14,7,1]
  reminder_days_before?: number[] | null;
}

/**
 * Permit update request (frontend → backend)
 * All fields optional for partial updates
 */
export interface PermitUpdate {
  permit_type?: PermitType;
  case_number?: string | null;
  permit_number?: string | null;
  reviewer_name?: string | null;
  date_submitted?: string | null;
  date_issued?: string | null;
  date_closed?: string | null;

  // V008 fields - Phase 6 Batch 2
  due_date?: string | null;
  recurring_period?: RecurringPeriod | null;
  fees_costs?: number | null;

  notes?: string | null;

  // V052: Applicant responsible (9.9e)
  applicant_organization_id?: string | null;

  // 9.16b: Permit type definition reference and custom data
  permit_type_definition_id?: string | null;
  custom_data?: Record<string, unknown> | null;
}

/**
 * Complete permit object (backend → frontend)
 */
export interface Permit extends PermitBase {
  id: string; // UUID
  tenant_id: string;
  project_id: string; // UUID
  created_at: string; // ISO datetime string
  updated_at: string; // ISO datetime string
  status: PermitStatus; // Calculated from dates

  // Phase 8.2: Renewal tracking fields
  renewed_from_permit_id?: string | null; // UUID of permit this was renewed from
  renewed_by_permit_id?: string | null; // UUID of permit that renewed this one
  renewed_at?: string | null; // ISO datetime when renewal occurred
  renewed_by_user_email?: string | null; // Email of user who created renewal

  // V052: Applicant organization name (joined from backend - 9.9e)
  applicant_organization_name?: string | null;

  // 9.16b: Permit type definition reference and custom data
  permit_type_definition_id?: string | null;
  custom_data?: Record<string, unknown> | null;
}

/**
 * API response for list permits (project-scoped)
 */
export interface ListPermitsResponse {
  success: boolean;
  permits: Permit[];
  total: number;
  project_id: string;
}

/**
 * Permit with joined project/client info (Phase 9.16b standalone list)
 */
export interface PermitWithProject extends Permit {
  project_number: number;
  project_name: string;
  client_name?: string | null;
  permit_type_definition_name?: string | null;
  permit_type_field_schema?: FieldSchema | null;
  display_status: PermitStatus; // Calculated status for display
  custom_data?: Record<string, unknown>;
}

/**
 * Field definition for permit type custom fields (Phase 9.16a)
 */
export interface FieldDefinition {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'dropdown' | 'checkbox';
  required?: boolean;
  placeholder?: string;
  options?: string[]; // For dropdown type
  default_value?: unknown;
}

/**
 * Field schema for permit type definitions (Phase 9.16a JSONB)
 */
export interface FieldSchema {
  fields: FieldDefinition[];
}

/**
 * Permit type definition (Phase 9.16a)
 * Represents a tenant-configurable permit type with custom fields
 */
export interface PermitTypeDefinition {
  id: string;
  tenant_id: string;
  name: string;
  category?: string | null;
  field_schema: FieldSchema;
  is_system_default: boolean;
  is_active: boolean;
  is_internal_milestone: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * API response for permit types list
 */
export interface PermitTypesResponse {
  success: boolean;
  permit_types: PermitTypeDefinition[];
  total: number;
  tenant_id: string;
  timestamp: string;
}

/**
 * Query options for standalone permit list (Phase 9.16b)
 */
export interface AllPermitsQueryOptions {
  search?: string;
  permitTypeDefinitionId?: string;
  status?: PermitStatus;
  dueBefore?: string; // ISO date
  dueAfter?: string; // ISO date
  limit?: number;
  offset?: number;
  sortBy?: 'due_date' | 'created_at' | 'permit_type' | 'project_number';
  sortOrder?: 'asc' | 'desc';
}

/**
 * API response for all permits list (Phase 9.16b)
 */
export interface AllPermitsResponse {
  success: boolean;
  permits: PermitWithProject[];
  total: number;
  limit: number;
  offset: number;
  timestamp: string;
}

/**
 * API response for single permit operations (get, create, update)
 */
export interface PermitResponse {
  success: boolean;
  permit: Permit;
  timestamp?: string;
}

/**
 * API response for delete permit
 */
export interface DeletePermitResponse {
  success: boolean;
  message: string;
}

/**
 * Helper type for permit form values (used in React Hook Form, etc.)
 */
export interface PermitFormValues {
  permit_type: PermitType;
  case_number: string;
  permit_number: string;
  reviewer_name: string;
  date_submitted: Date | null;
  date_issued: Date | null;
  date_closed: Date | null;

  // V008 fields - Phase 6 Batch 2
  due_date: Date | null;
  recurring_period: string;
  fees_costs: number | null;

  notes: string;
}

/**
 * Permit status badge color mapping (for UI components)
 */
export const PERMIT_STATUS_COLORS: Record<PermitStatus, string> = {
  not_submitted: 'gray',
  submitted: 'blue',
  issued: 'green',
  closed: 'slate',
};

/**
 * Permit status display labels
 */
export const PERMIT_STATUS_LABELS: Record<PermitStatus, string> = {
  not_submitted: 'Not Submitted',
  submitted: 'Submitted',
  issued: 'Issued',
  closed: 'Closed',
};

/**
 * Permit type display order (for dropdowns)
 */
// QW-5: Added 'Time Limited Development Permit' (Christa feedback 2024-12-22)
// 9.16c: Added 'LCRB Job' for Banks Architecture
export const PERMIT_TYPES: PermitType[] = [
  'Building Permit',
  'Development Permit',
  'DB Permit',
  'Occupant Load',
  'Private Patio DP',
  'Time Limited Development Permit',
  'Inquiry',
  'Alternate Solution',
  'Awning',
  'Signage',
  'LCRB Job',
  'Other',
];

/**
 * Recurring period options (for dropdowns)
 * Ordered by frequency (most common first)
 */
export const RECURRING_PERIODS: RecurringPeriod[] = [
  'Every 6 months',
  'Annual',
  'Every 18 months',
  'Every 2 years',
  'Every 3 years',
  'Every 5 years',
];
